import Rectangle from '@/helper-classes/inspector/Rectangle';
import DataPanel from '@/helper-classes/inspector/DataPanel';
import DateInfo from '@/helper-classes/inspector/DateInfo';

const CHART_DATA_START = '<chartdata>';
const CHART_DATA_END = '</chartdata>';
const PRICE_DATA_START = '<pricedata>';
const PRICE_DATA_END = '</pricedata>';
const CANDLE_VOLUME_THRESHOLD = 3;

export default class ChartData {
  panelRectangle: Rectangle;
  width: number;
  height: number;
  barWidth: number;
  barGap: number;
  marginLeft: number;
  marginRight: number;
  priceData: DateInfo[] = [];
  barPeriod: string;
  isIntraday: boolean;
  isCandleVolume: boolean;
  totalVolume: number = 0;
  panels: DataPanel[] = [];
  hAxisPanels: DataPanel[] = [];

  constructor(inspectorChartDataString: string) {
    if (!inspectorChartDataString) {
      throw new Error('No chart data found.');
    }

    const chartDataStartIndex = inspectorChartDataString.indexOf(CHART_DATA_START);
    const chartDataEndIndex = inspectorChartDataString.indexOf(CHART_DATA_END);

    if (chartDataStartIndex === -1 || chartDataEndIndex === -1) {
      const firstOneHundredCharactersOfInspectorChartDataString = inspectorChartDataString.substring(0, Math.min(inspectorChartDataString.length, 100));

      throw new Error(`No chart data found. Beginning of inspectorChartDataString: ${firstOneHundredCharactersOfInspectorChartDataString}`);
    }

    const rawChartDataString = inspectorChartDataString.substring(chartDataStartIndex + CHART_DATA_START.length, chartDataEndIndex);
    const rawChartDataArray = rawChartDataString.split('|');

    const rawChartSizeDataArray = this.getInnerDataArrayFromDataArray(rawChartDataArray, 'SIZE');
    this.width = parseInt(rawChartSizeDataArray[0], 10);
    this.height = parseInt(rawChartSizeDataArray[1], 10);

    const rawChartXDataArray = this.getInnerDataArrayFromDataArray(rawChartDataArray, 'X');
    const x1 = parseInt(rawChartXDataArray[0], 10);
    const x2 = parseInt(rawChartXDataArray[1]);
    this.marginLeft = parseInt(rawChartXDataArray[2], 10);
    this.marginRight = parseInt(rawChartXDataArray[3], 10);

    const rawChartYDataArray = this.getInnerDataArrayFromDataArray(rawChartDataArray, 'Y');
    const yValues = rawChartYDataArray.map(yValue => parseInt(yValue, 10));

    // Make the content rectangle
    this.panelRectangle = new Rectangle(
      x1,
      yValues[0],
      x2 - x1,
      yValues[yValues.length - 1] - yValues[0]
    );

    const panelStrings = rawChartDataArray.filter(rawChartDataItem => rawChartDataItem.indexOf('PANEL') === 0 && rawChartDataItem.indexOf('PANELS') === -1);

    for (let i = 0; i < panelStrings.length; i++) {
      const dataPanel = new DataPanel(this, panelStrings[i], [yValues[i], yValues[i + 1]]);

      this.panels.push(dataPanel);

      if (dataPanel.maxY - dataPanel.bounds.y >= 21) {
        this.hAxisPanels.push(dataPanel);
      }
    }

    // D 123 5.0 0.0 36 646 201304150930 201310041600
    const barDataArray = this.getInnerDataArrayFromDataArray(rawChartDataArray, 'BARS');

    this.barPeriod = barDataArray[0];

    this.isIntraday =
      this.barPeriod === 'J' || // 1
      this.barPeriod === 'I' || // 5
      this.barPeriod === 'H' || // 10
      this.barPeriod === 'G' || // 15
      this.barPeriod === 'F' || // 30
      this.barPeriod === 'E' || // 60
      !isNaN(Number(this.barPeriod));

    this.barWidth = parseFloat(barDataArray[2]);
    this.barGap = parseFloat(barDataArray[3]);

    const priceDataStartIndex = inspectorChartDataString.indexOf(PRICE_DATA_START);
    const priceDataEndIndex = inspectorChartDataString.indexOf(PRICE_DATA_END);

    if (priceDataStartIndex === -1 || priceDataEndIndex === -1) {
      throw new Error('No price data found.');
    }

    const rawPriceDataString = inspectorChartDataString.substring(priceDataStartIndex + PRICE_DATA_START.length, priceDataEndIndex);
    const rawPriceDataArray = rawPriceDataString.split('|');
    let previousDateInfo: DateInfo | null = null;
    let previousDayDateInfo: DateInfo | null = null;
    let biggestWidth = -1;
    let smallestWidth = -1;

    for (const rawPriceDataItem of rawPriceDataArray) {
      const dateInfo = new DateInfo(rawPriceDataItem);
      this.totalVolume += dateInfo.volume;
      this.priceData.push(dateInfo);

      if (previousDateInfo) {
        const currentWidth = dateInfo.pixelStart - previousDateInfo.pixelStart;

        if (biggestWidth === -1) {
          biggestWidth = currentWidth;
          smallestWidth = currentWidth;
        } else {
          biggestWidth = Math.max(currentWidth, biggestWidth);
          smallestWidth = Math.min(currentWidth, smallestWidth);
        }
      }

      if (dateInfo.endDateID.date !== previousDateInfo?.endDateID.date) { // if intraday, use the last bar of the previous day
        previousDayDateInfo = previousDateInfo;
      }

      previousDateInfo = dateInfo;

      if (previousDayDateInfo === null) {
        continue;
      }

      const close = dateInfo.prices[3];
      const previousClose = previousDayDateInfo.prices[3];
      const percentChange = (close - previousClose) / previousClose * 100;
      dateInfo.percentChange = percentChange;
    }

    this.isCandleVolume = biggestWidth - smallestWidth > CANDLE_VOLUME_THRESHOLD;
  }

  public getInnerDataArrayFromDataArray(dataArray: string[], innerDataTitle: string) {
    const formattedInnerDataTitle = innerDataTitle.toUpperCase() + ' ';
    let blockContent;

    for (const dataItem of dataArray) {
      if (dataItem.toUpperCase().indexOf(formattedInnerDataTitle) === 0) {
        blockContent = dataItem.substring(formattedInnerDataTitle.length);
        break;
      }
    }

    if (!blockContent) {
      return [];
    }

    return blockContent.split(' ');
  }

  public getBarWidthForIndex(index: number) {
    let barW = this.barWidth;

    if (this.isCandleVolume) {
      const widthWithoutMargins = this.panelRectangle.width - (this.marginLeft + this.marginRight);
      const dateInfo = this.getDateInfoForIndex(index);

      if (dateInfo.volume === 0 || this.totalVolume === 0) {
        barW = widthWithoutMargins / this.priceData.length;
      } else {
        barW = widthWithoutMargins * (dateInfo.volume / this.totalVolume);
      }
    } else {
      let rightX = this.panelRectangle.x + this.panelRectangle.width - this.marginRight + (this.barGap / 2);
      let leftX;

      rightX += this.barGap / 2;

      for (let i = this.priceData.length - 1; i >= 0; i--) {
        leftX = rightX - (this.barGap + this.barWidth);

        if (i === index) {
          barW = (rightX - this.barGap / 2) - (leftX + (this.barGap / 2));
          break;
        }

        rightX = leftX;
      }
    }

    if (barW < 1) {
      barW = 1;
    }

    return barW;
  }

  public getIndexForX(x: number, shouldConstrain: boolean) {
    const firstX = this.getXForIndex(0);
    const lastX = this.getXForIndex(this.priceData.length - 1) + (this.getBarWidthForIndex(this.priceData.length - 1) / 2);

    if (x < firstX) {
      return shouldConstrain ? 0 : -1;
    }

    if (x > lastX) {
      return shouldConstrain ? this.priceData.length - 1 : -1;
    }

    // Try to calculate index based on how far through inX is as a percentage of the panel
    const percentThrough = (x - this.panelRectangle.x) / this.panelRectangle.width;
    let startIndex = Math.round((this.priceData.length - 1) * percentThrough);

    // Make sure we aren't ahead of the desired index due to rounding error
    startIndex -= 10;

    if (startIndex < 0) {
      startIndex = 0;
    }

    // Try from calculated starting index
    let index = this.getIndexForXStartIndex(x, startIndex);

    if (index !== -1) {
      return index;
    }

    if (startIndex > 0) {
      // Try from zero
      index = this.getIndexForXStartIndex(x, 0);

      if (index !== -1) {
        return index;
      }
    }

    return shouldConstrain ? this.priceData.length - 1 : -1;
  }

  public getIndexForXStartIndex(x: number, startIndex: number) {
    // Make sure the startIndex is not for a DateInfo with a pixel start greater than x
    let pixelStart = this.priceData[startIndex].pixelStart - (this.barWidth / 2);

    while (pixelStart + this.barWidth + (this.barGap / 2) >= x && startIndex > 0) {
      startIndex--;
      pixelStart = this.priceData[startIndex].pixelStart - (this.barWidth / 2);
    }

    // Search for DateInfo corresponding to given x coordinate
    for (let i = startIndex; i < this.priceData.length; i++) {
      // pixelStart is actually 1/2 barWidth behind since we move the whole chart left by 1/2 barWidth on display
      pixelStart = this.priceData[i].pixelStart - (this.barWidth / 2);

      if (pixelStart + this.barWidth + (this.barGap / 2) >= x) {
        return i;
      }
    }

    return -1;
  }

  public getXForIndex(index: number) {
    if (index < 0) {
      return this.panelRectangle.x;
    }

    if (index > this.priceData.length - 1) {
      return this.panelRectangle.x + this.panelRectangle.width;
    }

    return this.priceData[index].pixelStart;
  }

  public getDateInfoForIndex(index: number) {
    if (index < 0) {
      index = 0;
    }

    if (index > this.priceData.length) {
      index = this.priceData.length - 1;
    }

    return this.priceData[index];
  }

  public getPanelForY(y: number) {
    for (const panel of this.panels) {
      const bounds = panel.bounds;

      if (y >= bounds.y && y < bounds.y + bounds.height) {
        return panel;
      }
    }

    return null;
  }
}
